'use client';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

type CarouselImage = {
  desktopUri: string;
  mobileUri?: string;
  order: number;
};

type SponsoredBanner = {
  id: string;
  title: string;
  bannerImageUri: string | null;
  desktopImageUri: string | null;
  mobileImageUri: string | null;
  bannerImages: CarouselImage[] | null;
  linkType: 'product' | 'collection' | 'brand' | 'custom';
  linkTarget: string | null;
  customUrl: string | null;
  position: string | null;
  order: number;
  hasCarousel?: boolean;
  desktopRatio?: string;
  mobileRatio?: string;
};

type SponsoredBannersContextType = {
  banners: SponsoredBanner[];
  loading: boolean;
  error: string | null;
  provided: boolean; // marker so children know a provider is present
};

const SponsoredBannersContext = createContext<SponsoredBannersContextType>({
  banners: [],
  loading: true,
  error: null,
  provided: false,
});

export function useSponsoredBanners() {
  return useContext(SponsoredBannersContext);
}

type SponsoredBannersProviderProps = {
  children: ReactNode;
  initialBanners?: SponsoredBanner[]; // Optional: pre-fetched banners from server
};

export function SponsoredBannersProvider({ children, initialBanners }: SponsoredBannersProviderProps) {
  const [banners, setBanners] = useState<SponsoredBanner[]>(initialBanners || []);
  const [loading, setLoading] = useState(true); // always fetch client-side; initialBanners are fallback
  const [error, setError] = useState<string | null>(null);
  const base = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';

  useEffect(() => {
    // Always fetch client-side so it shows in network tab
    // Use initial banners as fallback if fetch fails
    const fetchAllBanners = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const url = `${base}/api/sponsored-banners/active`;
        const res = await fetch(url, {
          cache: 'no-store',
          credentials: 'include',
        });

        if (!res.ok) {
          throw new Error(`Failed to fetch sponsored banners: ${res.status}`);
        }

        const data = await res.json();

        if (data?.success) {
          const fetchedBanners = data.banners || [];

          fetchedBanners.sort((a: SponsoredBanner, b: SponsoredBanner) => {
            if (a.order !== b.order) return a.order - b.order;
            return a.id.localeCompare(b.id);
          });

          setBanners(fetchedBanners);
        } else {
          throw new Error(data?.error || 'Failed to fetch sponsored banners');
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        setError(errorMessage);
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '[SponsoredBannersProvider] Failed to fetch banners; falling back to initial/empty state:',
            errorMessage
          );
        }
        // Fallback to initial banners if fetch fails
        if (initialBanners && initialBanners.length > 0) {
          setBanners(initialBanners);
        } else {
          setBanners([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAllBanners();
  }, [base, initialBanners]);

  return (
    <SponsoredBannersContext.Provider value={{ banners, loading, error, provided: true }}>
      {children}
    </SponsoredBannersContext.Provider>
  );
}

